import type { ActivityItem } from '@/features/home/lib/home-activity'
import type { PositionRisk } from '@/features/home/lib/home-risk'
import type { JournalSlice, TradeJournal } from '@/features/home/lib/home-trade-journal'

/**
 * The "What happened today?" prompt: the research assistant can't see the user's accounts, so
 * Home writes the snapshot into the question itself — value, PnL, each position with its
 * leverage and liquidation distance, the latest activity — and asks for a plain-language briefing.
 * English on purpose: it is the assistant's input, like the palette's prompt examples.
 */

export interface DaySummaryInput {
  equityUsd: number | null
  pnl1dUsd: number | null
  positions: readonly PositionRisk[]
  openOrderCount: number
  activity: readonly ActivityItem[]
}

const MAX_POSITIONS = 8
const MAX_ACTIVITY = 8

function usd(value: number): string {
  const sign = value < 0 ? '-' : ''
  return `${sign}$${Math.abs(value).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
}

function signedUsd(value: number): string {
  return value > 0 ? `+${usd(value)}` : usd(value)
}

function describePosition(risk: PositionRisk): string {
  const { position } = risk
  const details = [
    risk.leverage !== null ? `${risk.leverage}x leverage` : null,
    risk.unrealizedPnlUsd !== null ? `unrealized PnL ${signedUsd(risk.unrealizedPnlUsd)}` : null,
    risk.liquidationDistance !== null
      ? `liquidation ${(risk.liquidationDistance * 100).toFixed(1)}% away`
      : null,
  ].filter(Boolean)
  const size = position.quantity.replace(/^-/, '')
  return `- ${position.symbol}: ${risk.side} ${size}${details.length ? ` (${details.join(', ')})` : ''}`
}

const ACTIVITY_VERB: Record<ActivityItem['kind'], string> = {
  fill: 'filled',
  liquidation: 'liquidated',
  placed: 'placed',
  cancelled: 'cancelled',
  rejected: 'rejected',
}

function describeActivity(item: ActivityItem): string {
  const side = item.side ? `${item.side} ` : ''
  const price = item.price ? ` at ${item.price}` : ''
  return `- ${ACTIVITY_VERB[item.kind]}: ${side}${item.quantity} ${item.symbol}${price}`
}

export function buildDaySummaryPrompt(input: DaySummaryInput): string {
  const lines = ['Give me a short, beginner-friendly briefing on my trading today.']
  if (input.equityUsd !== null) {
    const pnl = input.pnl1dUsd !== null ? ` (24h PnL ${signedUsd(input.pnl1dUsd)})` : ''
    lines.push(`Portfolio value: ${usd(input.equityUsd)}${pnl}.`)
  }
  if (input.positions.length > 0) {
    lines.push('Open positions:')
    lines.push(...input.positions.slice(0, MAX_POSITIONS).map(describePosition))
  } else {
    lines.push('I have no open positions.')
  }
  lines.push(`Open orders: ${input.openOrderCount}.`)
  if (input.activity.length > 0) {
    lines.push('Latest activity:')
    lines.push(...input.activity.slice(0, MAX_ACTIVITY).map(describeActivity))
  }
  lines.push(
    'Explain what moved the markets I am exposed to, the biggest risks in my positions right now, and what I should keep an eye on.'
  )
  return lines.join('\n')
}

export interface HabitsInput {
  volumeUsd: number
  fillCount: number
  makerShare: number | null
  feesPaidUsd: number
  feeSavingsUsd: number
  topInstruments: readonly { symbol: string; volumeUsd: number; makerShare: number }[]
  upDays: number | null
  downDays: number | null
  maxDrawdownPercent: number | null
  leveragedPositions: number
  /** The fills paired into complete trades, when there are any. */
  journal?: TradeJournal | null
}

const HOLD_LABEL: Record<string, string> = {
  lt5m: 'under 5 min',
  m5to15: '5-15 min',
  m15to60: '15-60 min',
  h1to4: '1-4 hours',
  gt4h: 'over 4 hours',
}
const SESSION_LABEL: Record<string, string> = {
  asia: 'Asia 00-08 UTC',
  europe: 'Europe 08-13 UTC',
  us: 'US 13-21 UTC',
  late: 'late US 21-24 UTC',
}

function sliceLine(label: string, slice: JournalSlice): string {
  const rate = slice.winRate === null ? '' : `, right ${Math.round(slice.winRate * 100)}%`
  return `${label}: ${slice.trips} trades, ${signedUsd(slice.pnlUsd)}${rate}`
}

function journalLines(journal: TradeJournal): string[] {
  const lines = [
    `Complete trades (opened from flat, closed back to flat, after fees): ${journal.count}.`,
  ]
  if (journal.winRate !== null) {
    lines.push(`Right on ${Math.round(journal.winRate * 100)}% of them.`)
  }
  if (journal.avgWinUsd !== null && journal.avgLossUsd !== null) {
    lines.push(
      `Typical win ${usd(journal.avgWinUsd)}, typical loss ${usd(journal.avgLossUsd)} (win/loss ratio ${journal.payoff?.toFixed(2) ?? '-'}; break-even at this win rate needs ${journal.breakevenPayoff?.toFixed(2) ?? '-'}).`
    )
  }
  if (journal.profitFactor !== null) {
    lines.push(
      `Profit factor (winning dollars / losing dollars): ${journal.profitFactor.toFixed(2)}.`
    )
  }
  lines.push(
    `Net after fees: ${signedUsd(journal.netPnlUsd)}; fees: ${usd(journal.feesUsd)}; per trade: ${signedUsd(journal.expectancyUsd ?? 0)}.`
  )
  if (journal.medianHoldMs !== null) {
    lines.push(`Median hold: ${Math.round(journal.medianHoldMs / 60_000)} minutes.`)
  }
  lines.push(
    `By side: ${journal.bySide.map((slice) => sliceLine(slice.key, slice)).join('; ')}.`,
    `By hold time: ${journal.byHold
      .filter((slice) => slice.trips > 0)
      .map((slice) => sliceLine(HOLD_LABEL[slice.key], slice))
      .join('; ')}.`,
    `By session the trade was opened in: ${journal.bySession
      .filter((slice) => slice.trips > 0)
      .map((slice) => sliceLine(SESSION_LABEL[slice.key], slice))
      .join('; ')}.`,
    `By instrument: ${journal.byInstrument
      .slice(0, 6)
      .map((slice) => sliceLine(slice.key, slice))
      .join('; ')}.`
  )
  if (journal.worstDay) {
    lines.push(
      `Worst day: ${new Date(journal.worstDay.dayMs).toISOString().slice(0, 10)}, ${journal.worstDay.trips} trades, ${signedUsd(journal.worstDay.pnlUsd)}.`
    )
  }
  if (journal.afterLoss.trips > 0) {
    lines.push(sliceLine('Trades opened within 30 minutes of a loss', journal.afterLoss) + '.')
  }
  return lines
}

/**
 * "Write my full review": the last 30 days in numbers (volume, fees, and — when there are
 * complete trades — how often right, win vs loss, what paid and what leaked) and a request for
 * a plain-words review a newcomer can act on, like the one Alex shared.
 */
export function buildHabitsPrompt(input: HabitsInput): string {
  const lines = [
    'Review my trading over the last 30 days in plain words, like a coach writing for a newcomer. Start with one sentence that names the main thing paying me or costing me. Explain each term (win rate, win/loss ratio, profit factor, per-trade average) the first time you use it. Then say what paid, what cost money, give a short "Do this" and "Stop doing this" list, and say how often I should trade. Use only the numbers below and do not invent others. Keep tables to 6 rows or fewer.',
    `Volume: ${usd(input.volumeUsd)} over ${input.fillCount} fills.`,
  ]
  if (input.makerShare !== null) {
    lines.push(`Filled as maker: ${Math.round(input.makerShare * 100)}% of volume.`)
  }
  lines.push(
    `Fees paid: ${usd(input.feesPaidUsd)}; taker fees saved by filling as maker: ${usd(input.feeSavingsUsd)}.`
  )
  if (input.journal && input.journal.count > 0) lines.push(...journalLines(input.journal))
  if (input.topInstruments.length > 0) {
    lines.push(
      `Most traded by volume: ${input.topInstruments
        .map((i) => `${i.symbol} (${usd(i.volumeUsd)}, ${Math.round(i.makerShare * 100)}% maker)`)
        .join(', ')}.`
    )
  }
  if (input.upDays !== null && input.downDays !== null) {
    lines.push(`Account value: ${input.upDays} up days, ${input.downDays} down days.`)
  }
  if (input.maxDrawdownPercent !== null) {
    lines.push(`Worst drawdown: ${input.maxDrawdownPercent.toFixed(1)}%.`)
  }
  lines.push(
    `Positions flagged for high leverage or near liquidation right now: ${input.leveragedPositions}.`
  )
  return lines.join('\n')
}
